import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faEye,
    faFileCsv,
    faFileExcel,
    faFilePdf,
    faFilter,
    faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Table, Button, Form, Badge } from "@themesberg/react-bootstrap";
import {
    adminAllCourseList,
    adminGetReport,
    adminExportReport,
} from "../../../pages/api";

const ACTIVE_BUTTON_STYLE = {
    background: "linear-gradient(90deg, #2c60ba 0%, #005dfb 100%)",
    border: "none",
    borderRadius: "0.5rem",
    color: "#ffffff",
};

const REPORT_TYPES = [
    { value: "", label: "Select Report Type" },
    { value: "commission", label: "Commission" },
    { value: "course", label: "Course" },
    // { value: "earning_statement", label: "Earning Statement" },   
    { value: "opt_out", label: "Opt Out" },
    { value: "payment", label: "Payment" },
    // { value: "refund_requests", label: "Refund Requests" },
    { value: "registration", label: "Registration" },
    { value: "transaction", label: "Transaction" },
    { value: "wallet", label: "Wallet" },

    // { value: "projection", label: "Projection" },
];

const DEFAULT_FILTERS = {
    dateFrom: "",
    dateTo: "",
    course: "",
    status: "",
};

const getVisibleFilters = (reportType) => {
    switch (reportType) {
        case "payment":
        case "opt_out":
            return ["dateFrom", "dateTo", "course"];
        case "refund":
            return ["dateFrom", "dateTo", "course", "status"];
        case "course":
            return ["dateFrom", "dateTo", "course", "status"];
        case "registration":
            return ["dateFrom", "dateTo", "status"];
        case "settlement":
            return ["dateFrom", "dateTo", "status"];
        case "commission":
        case "transaction":
            return ["dateFrom", "dateTo"];
        case "projection":
        case "earning_statement":
        case "refund_requests":
            return ["dateFrom", "dateTo", "status"];

        case "wallet":
        default:
            return [];
    }
};

const getStatusOptions = (reportType) => {
    switch (reportType) {
        case "payment":
        case "refund":
            return [
                { value: "", label: "All Status" },
                { value: "success", label: "Success" },
                { value: "pending", label: "Pending" },
                { value: "failed", label: "Failed" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
            ];
        case "registration":
        case "earning_statement":
        case "refund_requests":
        case "course":
            return [
                { value: "", label: "All Status" },
                { value: "active", label: "Active" },
                { value: "inactive", label: "Inactive" },
                { value: "blocked", label: "Blocked" },
            ];
        case "settlement":
            return [
                { value: "", label: "All Status" },
                { value: "pending", label: "Pending" },
                { value: "completed", label: "Completed" },
                { value: "failed", label: "Failed" },
            ];
        default:
            return [];
    }
};

const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `₹${num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatDate = (value) => {
    if (!value) return "N/A" || "-";
    const normalized = String(value).includes(" ")
        ? String(value).replace(" ", "T")
        : value;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(date);
};

const capitalize = (value) =>
    String(value || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const formatLabel = (value) =>
    String(value || "N/A" || "-")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusBadgeStyle = (status) => {
    switch (String(status || "").toLowerCase()) {
        case "active":
        case "success":
        case "completed":
        case "generated":
        case "approved":
        case "processed":
            return { background: "#10b981", color: "#ffffff" };
        case "pending":
        case "estimated":
            return { background: "#f59e0b", color: "#ffffff" };
        case "inactive":
        case "blocked":
        case "failed":
        case "rejected":
            return { background: "#ef4444", color: "#ffffff" };
        default:
            return { background: "#6b7280", color: "#ffffff" };
    }
};

export default function AdminReports() {
    const [loading, setLoading] = useState(false);
    const [courseLoading, setCourseLoading] = useState(false);
    const [reportType, setReportType] = useState("");
    const [showReport, setShowReport] = useState(false);
    const [courses, setCourses] = useState([]);
    const [reports, setReports] = useState([]);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const dateFromRef = useRef(null);
    const dateToRef = useRef(null);

    const courseOptions = useMemo(
        () =>
            courses.map((course) => ({
                value: String(course.id),
                label: course.title,
            })),
        [courses]
    );

    const visibleFilters = useMemo(
        () => getVisibleFilters(reportType),
        [reportType]
    );

    const statusOptions = useMemo(
        () => getStatusOptions(reportType),
        [reportType]
    );

    const openDatePicker = (e) => {
        const input = e.currentTarget;
        if (typeof input.showPicker === "function") {
            try {
                input.showPicker();
            } catch (error) {
                // fallback to native behavior
            }
        }
    };

    const fetchCourses = async () => {
        try {
            setCourseLoading(true);
            const response = await adminAllCourseList({ pagination: false });
            const responseData = response?.data || {};
            const apiData = responseData?.data || {};
            const apiCourses = Array.isArray(apiData?.courses)
                ? apiData.courses
                : Array.isArray(apiData)
                    ? apiData
                    : [];
            setCourses(apiCourses);
        } catch (error) {
            console.error("Failed to fetch courses:", error);
            toast.error(error?.response?.data?.message || "Unable to load courses.");
            setCourses([]);
        } finally {
            setCourseLoading(false);
        }
    };

    const fetchReports = async (
        customFilters = filters,
        selectedReportType = reportType,
        page = 1,
        perPage = pagination.per_page || 10
    ) => {
        if (!selectedReportType) return;

        try {
            setLoading(true);

            const params = {
                report_type: selectedReportType,
                page,
                per_page: perPage,
            };

            if (customFilters.dateFrom) params.start_date = customFilters.dateFrom;
            if (customFilters.dateTo) params.end_date = customFilters.dateTo;
            if (customFilters.course) params.course_id = customFilters.course;
            if (customFilters.status) params.status = customFilters.status;

            const response = await adminGetReport(params);
            const responseData = response?.data || {};
            const apiData = responseData?.data || {};
            const apiPagination = apiData?.pagination || {};
            const apiReports = Array.isArray(apiData?.reports)
                ? apiData.reports
                : Array.isArray(apiData)
                    ? apiData
                    : [];

            setReports(apiReports);

            setPagination({
                current_page: Number(apiPagination.current_page ?? page ?? 1),
                last_page: Number(apiPagination.last_page ?? 1),
                per_page: Number(apiPagination.per_page ?? perPage ?? 10),
                total: Number(apiPagination.total ?? apiReports.length),
            });

            setShowReport(true);
        } catch (error) {
            console.error("Failed to fetch reports:", error);
            setReports([]);
            setPagination({
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
            });
            setShowReport(true);
            toast.error(error?.response?.data?.message || "Unable to load report.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!reportType) {
            setShowReport(false);
            setReports([]);
            setFilters(DEFAULT_FILTERS);
            return;
        }

        setFilters(DEFAULT_FILTERS);
        setPagination((prev) => ({ ...prev, current_page: 1 }));

        if (["payment", "refund", "course", "opt_out"].includes(reportType) && courses.length === 0) {
            fetchCourses();
        }

        fetchReports(DEFAULT_FILTERS, reportType, 1, pagination.per_page || 10);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportType]);

    const handleReportTypeChange = (value) => {
        setReportType(value);
    };

    const handleFilterChange = (key, value) => {
        const nextFilters = {
            ...filters,
            [key]: value,
        };

        setFilters(nextFilters);
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        fetchReports(nextFilters, reportType, 1, pagination.per_page || 10);
    };

    const handleViewReport = () => {
        if (!reportType) {
            toast.error("Please select report type.");
            return;
        }
        fetchReports(filters, reportType, 1, pagination.per_page || 10);
    };

    const buildExportParams = () => {
        const params = {
            report_type: reportType,
        };

        if (filters.dateFrom) params.start_date = filters.dateFrom;
        if (filters.dateTo) params.end_date = filters.dateTo;
        if (filters.course) params.course_id = filters.course;
        if (filters.status) params.status = filters.status;
        if (pagination.per_page) params.per_page = pagination.per_page;

        return params;
    };

    const downloadBlobFile = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleExport = async (format) => {
        if (!reportType) {
            toast.error("Please select report type first.");
            return;
        }

        try {
            const params = buildExportParams();
            params.format = format;

            const response = await adminExportReport(params);
            const blob = response?.data;

            if (!blob) {
                toast.error("Export file not received.");
                return;
            }

            const fileExt = format === "pdf" ? "pdf" : format === "csv" ? "csv" : "xlsx";
            downloadBlobFile(blob, `${reportType}-report.${fileExt}`);
            toast.success("Report exported successfully.");
        } catch (error) {
            console.error("Export failed:", error);
            toast.error(error?.response?.data?.message || "Unable to export report.");
        }
    };

    const getColumns = () => {
        switch (reportType) {
            case "payment":
            case "refund":
                return [
                    "Sr No",
                    "User",
                    "Customer ID",
                    "Email",
                    "Mobile Number",
                    "Course",
                    "Amount",
                    "Status",
                    "Date",
                ];
            case "commission":
                return ["Sr No", "To User", "From User", "Amount", "Level", "Date"];
            case "wallet":
                return ["Sr No", "User", "Customer ID", "Balance"];
            case "registration":
                return ["Sr No", "Name", "Email", "Customer ID", "Status", "Date"];
            case "transaction":
                return ["Sr No", "User", "Customer ID", "Type", "Amount", "Source", "Payment Method", "Status", "Date"];
            case "settlement":
                return ["Sr No", "User", "Customer ID", "Amount", "Settlement Type", "Status", "Date"];
            case "projection":
            case "earning_statement":
                return ["Sr No", "User", "Customer ID", "Cycle", "Net Earning", "Status", "Date"];
            case "refund_requests":
                return ["Sr No", "User", "Customer ID", "Refund Amount", "Status", "Date"];
            case "course":
                return ["Sr No", "User", "Customer ID", "Course", "Amount", "Date"];
            case "settlement":
                return ["Sr No", "User", "Customer ID", "Cycle", "Net Earning", "Date"];
            case "opt_out":
                return ["Sr No", "User", "Customer ID", "Email", "Mobile Number", "Course", "Amount", "Purchase Date", "Opt Out Date"];
            default:
                return [];
        }
    };

    const renderCell = (item, column) => {
        switch (column) {
            case "User":
                return item.user ?? "N/A";
            case "Email":
                return item.email ?? "N/A";
            case "Mobile Number":
                return item.mobile_number ?? "N/A";
            case "Course":
                return item.course ?? "N/A";
            case "Amount":
                return formatCurrency(item.amount);
            case "Status":
                return (
                    <Badge
                        className="rounded-pill d-inline-flex align-items-center justify-content-center"
                        style={{
                            ...getStatusBadgeStyle(item.status),
                            minWidth: "5.5rem",
                            height: "2rem",
                            padding: "0 12px",
                        }}
                    >
                        {formatLabel(item.status)}
                    </Badge>
                );
            case "Date":
                return formatDate(item.date);
            case "To User":
                return item.to_user ?? "N/A";
            case "From User":
                return item.from_user ?? "N/A";
            case "Level":
                return item.level ?? "N/A";
            case "Balance":
                return formatCurrency(item.balance);
            case "Name":
                return item.name ?? "N/A";
            case "Type":
                return capitalize(item.type ?? "N/A");
            case "Source":
                return item.source ?? "N/A";
            case "Payment Method":
                return capitalize(item.payment_method ?? "N/A");
            case "Settlement Type":
                return formatLabel(item.settlement_type);
            case "Cycle":
                return formatLabel(item.cycle);
            case "Net Earning":
                return formatCurrency(item.net_earning);
            case "Refund Amount":
                return formatCurrency(item.refund_amount);
            case "Purchase Date":
                return formatDate(item.purchase_date);
            case "Opt Out Date":
                return formatDate(item.opt_out_date);
            case "Customer ID":
                return item.custom_user_id ?? "N/A";
            
            case "Status":
                return (
                    <Badge
                        className="rounded-pill d-inline-flex align-items-center justify-content-center"
                        style={{
                            ...getStatusBadgeStyle(item.status),
                            minWidth: "5.5rem",
                            height: "2rem",
                            padding: "0 12px",
                        }}
                    >
                        {formatLabel(item.status)}
                    </Badge>
                );
            default:
                return "N/A" || "-";
        }
    };

    const totalPages = pagination.last_page || 1;
    const currentPage = pagination.current_page || 1;
    const startIndex = (currentPage - 1) * pagination.per_page;
    const paginatedData = reports;

    const getShowingRange = () => {
        const total = pagination.total || 0;
        const current = pagination.current_page || 1;
        const perPage = pagination.per_page || 10;

        if (total === 0) {
            return { start: 0, end: 0 };
        }

        const start = (current - 1) * perPage + 1;
        const end = Math.min(current * perPage, total);

        return { start, end };
    };

    const getPageItems = () => {
        const totalPagesValue = pagination.last_page || 1;
        const current = pagination.current_page || 1;

        if (totalPagesValue <= 5) {
            return Array.from({ length: totalPagesValue }, (_, i) => i + 1);
        }

        // Start pages
        if (current <= 2) {
            return [1, 2, 3, "...", totalPagesValue];
        }

        // End pages
        if (current >= totalPagesValue - 1) {
            return [1, "...", totalPagesValue - 2, totalPagesValue - 1, totalPagesValue];
        }

        // Middle pages: 1 ... previous current next ... last
        return [1, "...", current - 1, current, current + 1, "...", totalPagesValue];
    };

    const { start, end } = getShowingRange();
    const columns = getColumns();

    return (
        <>
            <div className="admin-reports-page">
                <div className="admin-reports-header py-4">
                    <div className="admin-reports-heading-wrap">
                        {/* <h5 className="admin-reports-title mb-1">Admin Reports</h5> */}
                        <h2 className="h4 mb-1 admin-reports-title">Admin Reports</h2>
                    </div>
                </div>

                <Card className="border-0 shadow-sm rounded-4 mb-4">
                    <Card.Body className="p-4">
                        <div className="admin-reports-filter-grid">
                            <div>
                                <Form.Label className="fw-semibold">
                                    Report Type <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={reportType}
                                    onChange={(e) => handleReportTypeChange(e.target.value)}
                                >
                                    {REPORT_TYPES.map((item) => (
                                        <option key={item.value || "empty"} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>

                            {visibleFilters.includes("dateFrom") && (
                                <div>
                                    <Form.Label className="fw-semibold">Date From</Form.Label>
                                    <Form.Control
                                        ref={dateFromRef}
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                                        onMouseDown={openDatePicker}
                                        disabled={!reportType}
                                    />
                                </div>
                            )}

                            {visibleFilters.includes("dateTo") && (
                                <div>
                                    <Form.Label className="fw-semibold">Date To</Form.Label>
                                    <Form.Control
                                        ref={dateToRef}
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                                        onMouseDown={openDatePicker}
                                        disabled={!reportType}
                                    />
                                </div>
                            )}

                            {visibleFilters.includes("course") && (
                                <div>
                                    <Form.Label className="fw-semibold">Course</Form.Label>
                                    <Form.Select
                                        value={filters.course}
                                        onChange={(e) => handleFilterChange("course", e.target.value)}
                                        disabled={!reportType || courseLoading}
                                    >
                                        <option value="">
                                            {courseLoading ? "Loading courses..." : "All Courses"}
                                        </option>
                                        {courseOptions.map((item) => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            )}

                            {visibleFilters.includes("status") && (
                                <div>
                                    <Form.Label className="fw-semibold">Status</Form.Label>
                                    <Form.Select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange("status", e.target.value)}
                                        disabled={!reportType}
                                    >
                                        {statusOptions.map((item) => (
                                            <option key={item.value || "all-status"} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                            )}
                        </div>

                        <div className="admin-reports-action-row mt-4">
                            {/* <Button
                                type="button"
                                style={ACTIVE_BUTTON_STYLE}
                                className="fw-semibold admin-view-report-btn"
                                onClick={handleViewReport}
                            >
                                <FontAwesomeIcon icon={faEye} className="me-2" />
                                View Report
                            </Button> */}

                            <div className="admin-reports-export-group">
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    disabled={!reportType}
                                    onClick={() => handleExport("csv")}
                                >
                                    <FontAwesomeIcon icon={faFileCsv} />
                                    {/* Export CSV */}
                                </Button>

                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    disabled={!reportType}
                                    onClick={() => handleExport("excel")}
                                >
                                    <FontAwesomeIcon icon={faFileExcel} />
                                    {/* Export Excel */}
                                </Button>

                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold"
                                    disabled={!reportType}
                                    onClick={() => handleExport("pdf")}
                                >
                                    <FontAwesomeIcon icon={faFilePdf} />
                                    {/* Print PDF */}
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {showReport && reportType ? (
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            <div className="admin-reports-table-top d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <div className="admin-reports-list-title-wrap">
                                    <h5 className="mb-1 text-capitalize">
                                        {reportType === "course"
                                            ? "Course"
                                            : reportType === "earning_statement"
                                                ? "Earning Statement"
                                                : reportType === "refund_requests"
                                                ? "Refund Requests"
                                                : reportType === "opt_out"
                                                ? "Opt Out"
                                                : reportType}{" "}
                                        Report
                                    </h5>
                                    {/* <div className="text-muted small">
                                        Filtered results for selected report type
                                    </div> */}
                                </div>

                                {/* <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="fw-semibold admin-print-btn"
                                    onClick={() => window.print()}
                                >
                                    <FontAwesomeIcon icon={faPrint} className="me-2" />
                                    Print
                                </Button> */}
                            </div>

                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 admin-reports-table">
                                    <thead>
                                        <tr>
                                            {columns.map((col) => (
                                                <th
                                                    key={col}
                                                    className={
                                                        col === "Amount" || col === "Balance"
                                                            ? "text-end"
                                                            : col === "Status"
                                                                ? "text-center"
                                                                : ""
                                                    }
                                                >
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={columns.length} className="text-center text-muted py-5">
                                                    Loading report data...
                                                </td>
                                            </tr>
                                        ) : paginatedData.length > 0 ? (
                                            paginatedData.map((item, index) => (
                                                <tr key={item.id ?? index}>
                                                    {columns.map((col) => (
                                                        <td
                                                            key={`${item.id ?? index}-${col}`}
                                                            className={
                                                                col === "Amount" || col === "Balance"
                                                                    ? "text-end"
                                                                    : col === "Status"
                                                                        ? "text-center"
                                                                        : ""
                                                            }
                                                        >
                                                            {col === "Sr No" ? startIndex + index + 1 : renderCell(item, col)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={columns.length} className="text-center text-muted py-5">
                                                    No records found for selected filters.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                                <div className="text-muted small">
                                    Showing {start} to {end} of {pagination.total} entries
                                </div>

                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        disabled={pagination.current_page <= 1 || loading}
                                        onClick={() => {
                                            fetchReports(
                                                filters,
                                                reportType,
                                                Math.max(1, pagination.current_page - 1),
                                                pagination.per_page || 10
                                            );
                                        }}
                                        className="course-page-btn"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </Button>

                                    {getPageItems().map((item, index) =>
                                        item === "..." ? (
                                            <span key={`dots-${index}`} className="text-muted px-1">
                                                ...
                                            </span>
                                        ) : (
                                            <Button
                                                key={item}
                                                type="button"
                                                variant="outline-primary"
                                                disabled={loading}
                                                onClick={() => {
                                                    fetchReports(filters, reportType, item, pagination.per_page || 10);
                                                }}
                                                className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                                            >
                                                {item}
                                            </Button>
                                        )
                                    )}

                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        disabled={pagination.current_page >= pagination.last_page || loading}
                                        onClick={() => {
                                            fetchReports(
                                                filters,
                                                reportType,
                                                Math.min(pagination.last_page, pagination.current_page + 1),
                                                pagination.per_page || 10
                                            );
                                        }}
                                        className="course-page-btn"
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                ) : (
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-5 text-center text-muted">
                            <FontAwesomeIcon icon={faFilter} className="mb-3" style={{ fontSize: "1.5rem" }} />
                            <div className="fw-semibold">Select a report type to display data</div>
                        </Card.Body>
                    </Card>
                )}
            </div>

            <style>{`
        .admin-reports-page {
          width: 100%;
          padding-inline: 0;
        }

        .admin-reports-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: nowrap;
        }

        .admin-reports-heading-wrap {
          min-width: 0;
          flex: 0 0 auto;
        }

        .admin-reports-title {
          font-size: clamp(1.4rem, 2vw, 2rem);
          line-height: 1.2;
          word-break: break-word;
          white-space: nowrap;
        }

        .admin-reports-filter-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
        }

        .admin-reports-action-row {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .admin-view-report-btn {
          white-space: nowrap;
          flex: 0 0 auto;
        }

        .admin-reports-export-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: nowrap;
          white-space: nowrap;
        }

        .admin-reports-export-group .btn {
          white-space: nowrap;
          flex: 0 0 auto;
        }

        .admin-reports-table-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .admin-reports-list-title-wrap {
          min-width: 0;
          flex: 1 1 auto;
        }

        .admin-print-btn {
          white-space: nowrap;
          flex: 0 0 auto;
          margin-left: auto;
        }

        .course-page-btn {
          width: 2rem;
          height: 2rem;
          padding: 0;
          border-radius: 0.5rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .course-page-btn svg {
          font-size: 0.95rem;
        }

        @media (max-width: 991.98px) {
          .admin-reports-filter-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 767.98px) {
          .admin-reports-header {
            flex-wrap: wrap;
            align-items: flex-start;
            gap: 0.85rem;
          }

          .admin-reports-filter-grid {
            grid-template-columns: 1fr;
          }

          .admin-reports-action-row {
            align-items: flex-end;
            justify-content: flex-end;
          }

          .admin-view-report-btn {
            margin-left: auto;
          }

          .admin-reports-export-group {
            width: auto;
          }

          .admin-reports-export-group .btn {
            flex: 0 0 auto;
          }
        }

        @media (max-width: 575.98px) {
          .admin-reports-title {
            font-size: 1.35rem;
          }

          .admin-reports-action-row {
            flex-direction: column;
            align-items: flex-end;
          }

          .admin-view-report-btn {
            width: auto;
            align-self: flex-end;
          }

          .admin-reports-export-group {
            width: 100%;
            justify-content: flex-end;
            flex-wrap: nowrap;
          }
        }
      `}</style>
        </>
    );
}